/// <reference types="google.maps" />
import { useEffect, type HTMLAttributes, type Ref } from 'react'
import { useJsApiLoader, type Libraries } from '@react-google-maps/api'

import type { FoundPlace } from './distance'

const MAPS_LIBRARIES: Libraries = ['places']

export type PlaceAutocompleteElement = HTMLElement & { value: string }

export type PlaceSelectEvent = Event & { placePrediction: google.maps.places.PlacePrediction }

type PlaceAutocompleteProps = HTMLAttributes<PlaceAutocompleteElement> & {
  ref?: Ref<PlaceAutocompleteElement>
  placeholder?: string
  includedRegionCodes?: string[]
  'ongmp-select'?: (event: PlaceSelectEvent) => void
  'ongmp-error'?: () => void
}

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'gmp-place-autocomplete': PlaceAutocompleteProps
    }
  }
}

export function useGoogleMaps({ apiKey, country }: { apiKey: string; country: string }) {
  return useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: MAPS_LIBRARIES,
    region: country,
  })
}

declare global {
  interface Window {
    gm_authFailure?: () => void
  }
}

// Maps calls gm_authFailure when the key is refused, after the script has loaded.
export function useMapFailure({ loadError, onError }: { loadError: Error | undefined; onError: () => void }) {
  useEffect(() => {
    if (loadError) onError()
  }, [loadError, onError])

  useEffect(() => {
    const previous = window.gm_authFailure
    window.gm_authFailure = onError
    return () => {
      window.gm_authFailure = previous
    }
  }, [onError])
}

export async function placeFromPrediction(prediction: google.maps.places.PlacePrediction): Promise<FoundPlace | null> {
  const place = prediction.toPlace()
  await place.fetchFields({ fields: ['location'] })
  if (!place.location) return null
  return {
    lat: place.location.lat(),
    lng: place.location.lng(),
    name: prediction.mainText?.text ?? prediction.text.text,
  }
}

export type Found = { place: FoundPlace; label: string } | null

export async function firstPlaceFor({ input, country }: { input: string; country: string }): Promise<Found> {
  const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    includedRegionCodes: [country.toLowerCase()],
    sessionToken: new google.maps.places.AutocompleteSessionToken(),
  })
  const prediction = suggestions[0]?.placePrediction
  if (!prediction) return null
  const place = await placeFromPrediction(prediction)
  return place && { place, label: prediction.text.text }
}
